import { useState } from 'react';

// Stock Phase 1 — mark an item out (docs/STOCK-SPEC.md §7.2, §8).
// Tap item → reason → expected back → done. Three taps; search is the fallback,
// not the main path.

export const REASONS = [
  { code: 'sold_through',     label: 'Sold out' },
  { code: 'prep_not_done',    label: 'Prep / bake not done' },
  { code: 'delivery_missing', label: "Delivery didn't arrive" },
  { code: 'quality',          label: 'Quality — dumped' },
  { code: 'equipment',        label: 'Equipment down' },
  { code: 'seasonal',         label: 'Seasonal / pulled' },
  { code: 'other',            label: 'Other' },
];
export const reasonLabel = (code) => REASONS.find((r) => r.code === code)?.label || 'No reason yet';

const tile = {
  padding: '14px 12px', borderRadius: 10, border: '1px solid #e0e0e0', background: '#fff',
  fontSize: 15, fontWeight: 600, textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', minHeight: 56,
};
const chip = (on) => ({
  ...tile, background: on ? 'var(--forest)' : '#fff', color: on ? '#fff' : '#212121', borderColor: on ? 'var(--forest)' : '#e0e0e0',
});
const itemLabel = (i) => (i.variation_name && i.variation_name !== 'Regular' ? `${i.item_name} — ${i.variation_name}` : i.item_name);

// Reason + expected-back picker. Also used on the board to give a reason to an
// item that was rung out on the register.
export function ReasonPicker({ itemName, busy, onSubmit, onCancel }) {
  const [reason, setReason] = useState(null);
  const [note, setNote] = useState('');
  const [back, setBack] = useState('tomorrow');
  const needsNote = reason === 'other';
  const ready = reason && (!needsNote || note.trim());
  return (
    <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: 16 }}>
      <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 12 }}>Why is {itemName} out?</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
        {REASONS.map((r) => (
          <button key={r.code} style={chip(reason === r.code)} onClick={() => setReason(r.code)}>{r.label}</button>
        ))}
      </div>
      {needsNote && (
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What happened?" maxLength={300}
          style={{ marginTop: 12, width: '100%', padding: 12, fontSize: 15, borderRadius: 8, border: '1px solid #ccc', fontFamily: 'inherit' }} />
      )}
      {reason && reason !== 'seasonal' && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 13, color: '#757575', marginBottom: 6 }}>When is it back?</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={chip(back === 'tomorrow')} onClick={() => setBack('tomorrow')}>Back tomorrow at open</button>
            <button style={chip(back === 'none')} onClick={() => setBack('none')}>Not sure</button>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button disabled={!ready || busy} onClick={() => onSubmit({ reason, note: note.trim() || undefined, expected_back: back })}
          style={{ ...tile, flex: 1, textAlign: 'center', background: ready ? 'var(--forest)' : '#bdbdbd', color: '#fff', borderColor: 'transparent' }}>
          {busy ? 'Saving…' : 'Mark out'}
        </button>
        <button onClick={onCancel} style={{ ...tile, textAlign: 'center' }}>Cancel</button>
      </div>
    </div>
  );
}

export function StockMarkOut({ items, busy, onMark, onClose }) {
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState(null);
  const q = query.trim().toLowerCase();
  const shown = q ? items.filter((i) => itemLabel(i).toLowerCase().includes(q)) : items;

  if (picked) {
    return (
      <ReasonPicker itemName={itemLabel(picked)} busy={busy} onCancel={() => setPicked(null)}
        onSubmit={(r) => onMark(picked, r)} />
    );
  }
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search"
          style={{ flex: 1, padding: 12, fontSize: 15, borderRadius: 8, border: '1px solid #ccc', fontFamily: 'inherit' }} />
        <button onClick={onClose} style={{ ...tile, textAlign: 'center' }}>Back to board</button>
      </div>
      {shown.length === 0 && <div style={{ color: '#757575', padding: 12 }}>Nothing in stock matches.</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
        {shown.map((i) => (
          <button key={i.stock_item_id} style={tile} onClick={() => setPicked(i)}>{itemLabel(i)}</button>
        ))}
      </div>
    </div>
  );
}

export { itemLabel };
